import { createContext, useContext, useMemo } from 'react';
import { TAGS_ROTATION, TAGS_SPECIALTY } from '../constants';
import { sortByAlphabet } from '../utils';
import { useSelector } from 'react-redux';
import { getLang } from '../store/selectors/lang.selector';
import { getOrganizationId } from '../store/selectors/organization.selector';
import { nanoid } from 'nanoid';
import { useAllRotations } from './rotations.hooks';

const RotationOptionsContext = createContext();

// Safe fallback when no provider is mounted (e.g. mobile, which uses its own
// RotationOptionsContext and supplies rotationOptions separately). Prevents a
// crash in consumers that destructure the context value.
const EMPTY_ROTATION_OPTIONS = { rotationOptions: [], getFilteredRotationOptions: () => [] };

export function useRotationOptions() {
  return useContext(RotationOptionsContext) ?? EMPTY_ROTATION_OPTIONS;
}

const createOptions = ({
  values,
  labels = {},
  filter = item => item,
  getSufix = () => '',
  prefix = false,
}) => {
  return values
    .filter(filter)
    .map(name => ({
      key: nanoid(),
      value: prefix ? `rotation.${name}` : name,
      label: `${labels[name] ?? name}${getSufix(name)}`,
    }))
    .sort((a, b) => sortByAlphabet(a.label, b.label));
};

export function RotationOptionsProvider({ children }) {
  const LANG_TAGS = useSelector(getLang('TAGS'));
  const orgId = useSelector(getOrganizationId);
  const { data } = useAllRotations({ enabled: !!orgId });

  const { rotations, isDefault } = useMemo(() => {
    if (!data?.length) {
      return {
        rotations: Object.values({ ...TAGS_SPECIALTY, ...TAGS_ROTATION }),
        isDefault: true,
      };
    }
    return { rotations: data.map(r => r.name), isDefault: false };
  }, [data]);

  const rotationOptions = useMemo(
    () =>
      isDefault
        ? createOptions({ values: rotations, labels: LANG_TAGS })
        : createOptions({ values: rotations }),
    [rotations, isDefault, LANG_TAGS],
  );

  // Filter values based on user stats
  const getFilteredRotationOptions = (stats, value, showSufix = false) =>
    createOptions({
      values: rotations,
      labels: isDefault ? LANG_TAGS : {},
      filter: el => stats?.[value]?.[el],
      getSufix: el => (showSufix ? ` (${stats?.[value]?.[el] || 0})` : ''),
      prefix: true,
    });

  const value = {
    rotations,
    rotationOptions,
    getFilteredRotationOptions,
  };

  return (
    <RotationOptionsContext.Provider value={value}>{children}</RotationOptionsContext.Provider>
  );
}
